// 3c) Project-specific modules - components / types
// Using entity-types to avoid circular dependency
import type { CustomField, PageData, UserBase } from '../entity-types';
import type { Role } from '../roles/types';

// 3d) Utility functions / interfaces
import { DynamicObject } from '../../utils/commonInterfaces';

export enum CompaniesActionTypes {
    SET_COMPANY_LOADING_DATA = '@@companies/SET_COMPANY_LOADING_DATA',
    GET_USER_COMPANIES_REQUEST = '@@companies/GET_USER_COMPANIES_REQUEST',
    GET_USER_COMPANIES_SUCCESS = '@@companies/GET_USER_COMPANIES_SUCCESS',
    SELECT_USER_COMPANY_REQUEST = '@@companies/SELECT_USER_COMPANY_REQUEST',
    SELECT_USER_COMPANY_SUCCESS = '@@companies/SELECT_USER_COMPANY_SUCCESS',
    SAVE_USER_COMPANY_REQUEST = '@@companies/SAVE_USER_COMPANY_REQUEST',
    SAVE_USER_COMPANY_RESPONSE = '@@companies/SAVE_USER_COMPANY_RESPONSE',
    UPLOAD_COMPANY_LOGO_REQUEST = '@@companies/UPLOAD_COMPANY_LOGO_REQUEST',
    GET_SYSTEM_CONFIGURATION_SUCCESS = '@@companies/GET_SYSTEM_CONFIGURATION_SUCCESS',
    SAVE_COMPANY_ATB_SETTINGS_REQUEST = '@@companies/SAVE_COMPANY_ATB_SETTINGS_REQUEST',
    RESET_ALL_COMPANY_DATA_TABLE_FILTERS = '@@companies/RESET_ALL_COMPANY_DATA_TABLE_FILTERS',
    GET_COMPANY_WORKFLOW_OPTIONS_REQUEST = '@@companies/GET_COMPANY_WORKFLOW_OPTIONS_REQUEST',
    GET_COMPANY_WORKFLOW_OPTIONS_SUCCESS = '@@companies/GET_COMPANY_WORKFLOW_OPTIONS_SUCCESS',
    SAVE_COMPANY_USER_SETTINGS_REQUEST = '@@companies/SAVE_COMPANY_USER_SETTINGS_REQUEST',
    SAVE_COMPANY_USER_SETTINGS_RESPONSE = '@@companies/SAVE_COMPANY_USER_SETTINGS_RESPONSE',
    GET_CUSTOM_FIELDS_CHANGES_REQUEST = '@@companies/GET_CUSTOM_FIELDS_CHANGES_REQUEST',
    GET_CONVERA_CREDENTIALS_FOR_COMPANY_REQUEST = 'GET_CONVERA_CREDENTIALS_FOR_COMPANY_REQUEST',

}

export interface CompaniesState {
    readonly loading: boolean;
    readonly userCompanies: CompanyUserRole[];
    readonly selectedUserCompany: CompanyUserRole | undefined;
    readonly saveLoading: boolean;
    readonly systemConfiguration: SystemConfiguration | undefined;
    readonly workflowOptions: WorkflowOption[];
    readonly fullWorkflowOptions: WorkflowOption[];
    readonly workflowFetchLoading: boolean;
    readonly customWording: WordingItem;
    readonly wordingLoading: boolean;
    readonly questionAnswerItems: QuestionAnswerItem[] | undefined;
    readonly questionAnswerLoading: boolean;
    readonly companyStudentMenuItemsLoading: boolean;
    readonly companyStudentMenuItems: SidebarItem[];
}

export interface WorkflowOption {
    readonly WorkflowId: string;
    readonly WorkflowName: string;
    readonly Type: string;
}

export interface SystemConfiguration {
    readonly Environment: string;
    readonly LiveMode: boolean;
}

export interface Company {
    readonly CompanyId: string;
    readonly OrganisationId: string;
    readonly Name: string;
    readonly Email: string;
    readonly LogoPath: string;
    readonly CreatedDateTime: string;
    readonly UpdateDateTime: string;
    readonly AddressLine1: string;
    readonly AddressLine2: string;
    readonly Suburb: string;
    readonly State: string;
    readonly Postcode: string;
    readonly PhoneNumber: string;
    readonly ABN: string;
    readonly Timezone: string;
    readonly Industry: string;
    readonly PrimaryColorTheme: string;
    readonly SecondaryColorTheme: string;
    readonly CompanyContacts: CompanyContact[];
    readonly CustomFieldsConfigure: CompanyCustomFieldConfigure[];
    readonly IsDueDateView: boolean;
    readonly IsCalendarView: boolean;
    readonly IsGroupedATB: boolean;
    readonly IsSearchCustomFieldsByCheckbox: boolean;
    readonly UsingCustomerWorkflow: boolean;
    readonly LockedDeliveryMethod: boolean;
    readonly Region: string;
    readonly LanguagePackage: LanguagePackage;
    readonly PaymentOptions: DynamicObject;
    readonly ImportType: string;
    readonly CloudImportType: string;
    readonly ApiImportType: string;
    readonly EnableExternalUserTaskAssignment: boolean;
    readonly EnableCustomerPortal: boolean;
    readonly UsingMultipleWorkflow: boolean;
    readonly CustomerTitleLabel: string;
    readonly Country: string;
    readonly IsClosed?: boolean;
    readonly TotalUser?: number;
    readonly SupportSendNewInvoice: boolean;
    readonly SupportCashAllocation: boolean;
    readonly NotificationCustomization: CompanyNotificationCustomization;
    readonly EnableMultiFactorAuthentication: boolean;
    readonly InboundEmail?: string;
    readonly CommunicationMethod?: number;
    readonly CommunicationGroupSettings?: CommunicationGroupMethod;
    readonly CompanyPaymentPlan: CompanyPaymentPlan;
    readonly LanguageCode: string;
    readonly EnablePaymentBehaviourInsights: boolean;
    readonly EnableBankFileImporting: boolean;
    readonly UsingCustomBankFileProcessor: boolean;
    readonly CollectionEmail: string;
    readonly IncludeManualItemsATB: boolean;
    readonly IncludeRebatesATB: boolean;
    readonly TimeThresholdForCancellingTasksInProgress?: number;
    readonly DisableCustomerTickets: boolean;
    readonly NotifyDailyImport: boolean;
    readonly StudentPortalSidebarItems: SidebarItem[];
}

export interface CompanyCustomFieldConfigure {
    Type: string;
    Number: number;
    FieldName: string;
    CustomFieldValues?: CustomField[];
    children?: CustomField[];
    DisplayInNotification: boolean;
    CompanyId: string;
    DataType?: string;
    FilterType?: string;
    IsRequired?: string;
}

export interface CompanyUserRole {
    readonly CompanyUserRoleId: string;
    readonly User: UserBase;
    readonly Role: Role;
    readonly Company: Company;
    readonly CreatedDateTime: string;
    readonly UpdateDateTime: string;
}

export interface CompanyContact {
    readonly Name: string;
    readonly PhoneNumber: string;
    readonly Email: string;
    readonly Type: string;
}

export interface LanguagePackage {
    Language: string;
    Suburb: string;
    State: string;
    Postcode: string;
    CompanyRegistrationNumber: string;
    BankBSB: string;
    BankAccountNumber: string;
    DateFormat: string;
    CurrentDateFormat: string;
}

export interface CompanyNotificationCustomization {
    BrandingLocation?: string;
    OpeningWording?: string;
    IncludeLinkInLetterBlock?: boolean;
    InformUsInstruction?: string;
    InformUsTextLabel?: string;
    CreditUsageWording?: string;
    CardPaymentCustomization: CardPaymentCustomization;
    MailingChequePaymentCustomization: MailingChequePaymentCustomization;
    BankPaymentCustomization: BankPaymentCustomization;
}

export interface CardPaymentCustomization {
    Heading: string;
    Description: string;
}

export interface MailingChequePaymentCustomization {
    Heading: string;
    Description: string;
}

export interface BankPaymentCustomization {
    Heading: string;
    Description: string;
}

export interface CompanyPaymentPlan {
    IsEnabled: boolean;
}

export interface CompanyCalendarView {
    readonly CompanyId: string;
    readonly IsCalendarView: boolean;
}

export interface CommunicationGroupMethod {
    readonly CustomerGroup: number;
    readonly CommunicationStateGroup: number;
}

export const customFieldsSource = {
   
    Import :'Import',
    Customer :'Customer',
    User : "User",
    PaymentURLClick : "PaymentURLClick",
    System : "System",
    Email : "Email"
};

export const customFieldChangeTypes = {
    NewResource: 'NewResource',
    UpdatedResource: 'UpdatedResource',
    Allocation: 'Allocation',
    Deallocation: 'Deallocation',
    Settlement: 'Settlement',
    ResourceLink: 'ResourceLink',
    Verified: 'Verified',
    Processed: 'Processed',
    Disregarded: 'Disregarded',
};

export interface CustomFieldChangesChangesState {
    readonly loading: boolean;
    readonly errorMessages: string[];
    readonly data: [];
    readonly pageData: PageData;
    readonly filters: [];
    readonly customFieldsChangesTableFilter: string;
    readonly sortBy: string;
    readonly sortAscending: boolean;
}

export interface GetCustomFieldChangesRequestPayload {
    filters: {};
    pageSize: number;
    currentPage: number;
}

export interface WordingItem {
    [key: string]: string;
};

export interface WordingItemRow {
    key: string;
    value: string;
    id: string;
    index: number;
    _touched?: boolean;
};

export interface SidebarItem {
    Route: string;
    Name: string;
    Visible: boolean;
    Description: string;
    Parent: string; 
};

export interface GroupedSidebarItems {
    rootItem: SidebarItem;
    childItems?: SidebarItem[];
};

export interface QuestionAnswerItem {
    Question: string;
    Answer: string;
};

export interface QuestionAnswerFormItem {
    Id: string;
    Question: string;
    Answer: string;
    _touched?: boolean;
};